import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { calendarTabs } from 'pages/tours/tours-calendar-page/model'

import { IRange } from 'widgets/tour/date-range-picker'
import { ButtonCalendar } from 'widgets/tour/date-range-picker/ components'

import { DateAndNights } from 'features/tours/ui/date-and-nights'

import {
  getButtonText,
  getNightsText,
} from 'entities/tour/libs/getNightsDateText.ts'
import searchTour from 'entities/tour/models/search-tour/search-tour'

import { SearchToursApi } from 'shared/api/tours/search'
import { flexibleDateRange } from 'shared/constants/tours'
import ImageBag from 'shared/images/bag.svg'
import { EmptyResults } from 'shared/ui/empty-result'
import { FlexibleDepartureCell } from 'shared/ui/flexible-departure-cell'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Paper } from 'shared/ui/paper'
import { RadioButton } from 'shared/ui/radio-button'

import { SelectNightsCountSkeleton } from '../select-nights-count-skeleton'

import s from './styles.module.scss'

export const SelectNightsCount = observer(() => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [nights, setNights] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const formSearch = searchTour.getEditableDataForSearchTour()

  const selectedDate: IRange = {
    startDate: formSearch?.flight?.startDay,
  }
  const haveResults = !!nights?.length
  const isFlexibleNights = searchTour.getIsFlexibleNights()
  const isFlexibleDate = searchTour.getIsFlexibleDate()

  const [selectedDuration, setSelectedDuration] = useState<number | undefined>(
    formSearch?.duration,
  )

  const getAllNights = async () => {
    setIsLoading(true)
    try {
      const { data: nights } = await SearchToursApi.getNights()

      setNights(nights?.data)
    } finally {
      setIsLoading(false)
    }
  }

  const onSelectDuration = (index: number) => {
    const newSelectedDurations = nights?.[index]

    setSelectedDuration(newSelectedDurations)

    searchTour.setDurationResort(newSelectedDurations)
  }

  const onContinueSelectDuration = () => {
    navigate(-1)
  }

  const onToggleFlexibleNights = (isEnabled: boolean) => {
    searchTour.setIsFlexibleNights(isEnabled)
  }

  const tabsHandler = (value: string) => {
    navigate(`${RoutingPaths.tours.path()}${value}`, { replace: true })
  }

  const onGoBack = () => {
    navigate(-1)
  }

  useEffect(() => {
    void getAllNights()
  }, [])

  const nightsText = getNightsText({
    t,
    nights: formSearch?.duration,
    isFlexibleNights,
  })

  const dateText = getButtonText(selectedDate, isFlexibleDate)

  return (
    <div className={s.container}>
      <div className={s.box}>
        <HeaderGoBack
          onGoBack={onGoBack}
          buttonWhite
          variant="grey"
          title={t('tours_block.select_date')}
        />
      </div>

      {haveResults && !isLoading && (
        <>
          <div className={s.tabs}>
            <DateAndNights
              nightsText={nightsText}
              dateText={dateText}
              onClick={tabsHandler}
              defaultActive={calendarTabs[1].path}
            />
          </div>

          <div className={s.flexibleDeparture}>
            <FlexibleDepartureCell
              title={t('tours_block.flexible_nights', {
                days: flexibleDateRange,
              })}
              className={s.flexibleDeparture}
              isEnabled={!!isFlexibleNights}
              isCard={false}
              onToggle={onToggleFlexibleNights}
            />
          </div>
        </>
      )}

      {isLoading ? (
        <SelectNightsCountSkeleton />
      ) : (
        <Paper
          padding={[14, 16]}
          className={s.listNights}
        >
          {haveResults ? (
            nights?.map((duration, index) => (
              <div
                key={duration}
                className={s.selectNightsItem}
                onClick={() => onSelectDuration(index)}
              >
                <p className={clsx('medium-16', s.selectNightsText)}>
                  {t('tours_block.night', { count: duration })}
                </p>

                <RadioButton isActive={selectedDuration === duration} />
              </div>
            ))
          ) : (
            <div className={s.empty}>
              <EmptyResults
                image={
                  <img
                    src={ImageBag}
                    alt="bag"
                    className={s.image}
                  />
                }
                title={t('tours_block.not_found')}
                subTitle={t('tours_block.try_again')}
              />
            </div>
          )}
        </Paper>
      )}

      <ButtonCalendar
        onClick={onContinueSelectDuration}
        disabled={!selectedDuration || isLoading}
      />
    </div>
  )
})
